import { CompoundFilterState, CompoundFilterStateActions } from './CompoundFilter.types';

import { clearOptions } from './CompoundFilter.reducer.helpers';
import { resolveCompoundFilterStateModifiers } from './helpers';

export const compoundFilterReducer = (
  state: CompoundFilterState,
  action: CompoundFilterStateActions,
  onChange: (state: CompoundFilterState, action: CompoundFilterStateActions) => void,
): CompoundFilterState => {
  switch (action.type) {
    case 'TOGGLE_ITEM': {
      const itemIndex = action.payload;

      const { isActive } = resolveCompoundFilterStateModifiers(state[itemIndex]?.stateModifiers);

      const newState = state.map((item, index) => {
        return {
          ...item,
          /**
           * This code block handles user interactions with items by toggling the selected item if its index matches the payload.
           * If the index does not match, it sets all other items to false to ensure that only the correct options are displayed.
           */
          stateModifiers: {
            ...item.stateModifiers,
            isActive: index === itemIndex ? !isActive : false,
          },
        };
      });

      onChange(newState, action);
      return newState;
    }
    case 'TOGGLE_OPTION': {
      const { itemIndex, optionIndex } = action.payload;

      const { isActive, isToggleable } = resolveCompoundFilterStateModifiers(
        state[itemIndex].options[optionIndex]?.stateModifiers,
      );

      if (!isToggleable) {
        return state;
      }

      const newState = state.map((item, itemIdx) => {
        item.options = item.options.map((option, optionIdx) => {
          if (itemIdx === itemIndex && optionIdx === optionIndex) {
            return { ...option, stateModifiers: { ...option.stateModifiers, isActive: !isActive } };
          }

          return option;
        });

        return item;
      });

      onChange(newState, action);
      return newState;
    }
    case 'ADD_OPTION': {
      const { itemIndex, newOption } = action.payload;

      const isFound = state[itemIndex].options.find((x) => x.id === newOption.id);

      if (isFound) {
        return state;
      }

      const newState = [...state];
      const newOptions = [...newState[itemIndex].options];

      newOptions.push(newOption);

      newState[itemIndex].options = newOptions;

      onChange(newState, action);
      return newState;
    }
    case 'REMOVE_OPTION': {
      const { itemIndex, optionIndex } = action.payload;

      const newState = [...state];

      newState[itemIndex].options = newState[itemIndex].options.filter((option, index) => {
        const { isRemovable } = resolveCompoundFilterStateModifiers(option.stateModifiers);

        if (isRemovable === false) {
          return true;
        }

        return index !== optionIndex;
      });

      onChange(newState, action);
      return newState;
    }

    /**
     * This action clears options by filtering out clearable options
     * and setting the active status of remaining options to false.
     * This provides a quick way to toggle off all options.
     */
    case 'CLEAR_OPTIONS': {
      const itemIndex = action.payload;

      const newState = [...state];

      newState[itemIndex].options = clearOptions(newState[itemIndex].options);

      onChange(newState, action);
      return newState;
    }

    case 'CLEAR_ALL': {
      const newState = state.map((item) => {
        const stateModifiers = { ...item.stateModifiers, isActive: false };

        item.stateModifiers = stateModifiers;
        item.options = clearOptions(item.options);

        return item;
      });

      onChange(newState, action);
      return newState;
    }

    case 'UPDATE_OPTION': {
      const { itemIndex, optionIndex, data } = action.payload;
      const newState = [...state];

      // @ts-expect-error - This is intentional to prevent the user from updating the option id.
      if (data?.id)
        // @ts-expect-error - This is intentional to prevent the user from updating the option id.
        delete data.id;

      if (newState?.[itemIndex]?.options?.[optionIndex])
        newState[itemIndex].options[optionIndex] = {
          ...newState[itemIndex].options[optionIndex],
          ...data,
          stateModifiers: {
            ...newState[itemIndex].options[optionIndex].stateModifiers,
            ...data?.stateModifiers,
          },
        };

      onChange(newState, action);
      return newState;
    }

    default:
      return state;
  }
};
